<template>
  <div class="lad-landing-cover__custom">
    <el-carousel
      indicator-position="none"
      arrow="never"
      @change="handleChangeCarousel"
    >
      <el-carousel-item
        v-for="(itinerary, i) in exItinerary"
        :key="`carousel-itinerary-${i}`"
      >
        <LadImage
          :max-width="isMobile ? 400 : 1200"
          :min-height="600"
          :include-all-sources="true"
          :lazy-loading="true"
          :preloading="true"
          :src="getImageDay(itinerary) || exCover.url"
          :title="exCover.title"
          :alt="exCover.title"
          :quality="isMobile ? 60 : 80"
        />
      </el-carousel-item>
    </el-carousel>

    <div class="overlay"></div>

    <div class="lad-landing-cover__custom__content">
      <div>
        <nuxt-link
          to="/"
          :prefetch="false"
          title="LocalAdventures"
          class="landing-url-home"
          @click="handleClickLogo"
        >
          <svg-icon
            name="landing/logo-white"
            class="sprite-icons landing-logo"
          />
        </nuxt-link>
      </div>

      <div>
        <h2
          >{{
            $tc(`${base}.days`, exExperience.duration, {
              days: exExperience.duration,
            })
          }}
          •
          {{
            $tc(`${base}.city`, exExperience.destinations.length, {
              total: exExperience.destinations.length,
            })
          }}</h2
        >
        <h1>{{
          $t(`${base}.title`, {
            name: exExperience.name,
          })
        }}</h1>
        <p>{{ getHighlights }}</p>
        <div class="medias">
          <span>{{ getLocationDay }}</span>
          <div :class="{ actions: true, 'without-video': !hasVideo }">
            <el-button
              type="primary"
              icon="el-icon-picture-outline"
              :circle="isMobile"
              :round="!isMobile"
              @click="handleShowPictures"
              >{{
                isMobile ? '' : $tc(`${base}.button.view_pictures`)
              }}</el-button
            >
            <div v-if="hasVideo"></div>
            <el-button
              v-if="hasVideo"
              icon="el-icon-video-camera"
              :circle="isMobile"
              :round="!isMobile"
              @click="handleShowVideo"
              >{{ isMobile ? '' : $tc(`${base}.button.view_video`) }}</el-button
            >
          </div>
        </div>
      </div>
    </div>

    <LazyHydrate never :trigger-hydration="dialogs.pictures">
      <LadExperienceGallery
        v-if="dialogs.pictures"
        :open="dialogs.pictures"
        :with-main-image="false"
        @close="handleClosePictures"
      />
    </LazyHydrate>

    <LazyHydrate never :trigger-hydration="dialogs.video">
      <LadVideoDialog :open="dialogs.video" @close="handleCloseVideo" />
    </LazyHydrate>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CacheComponentMixin from '@/utils/mixin/cache-components'
import { LadVideoDialog } from '@/components/pages/landing/video'
import GenericEventMixin from '@/utils/mixin/generic-event'

export default {
  name: 'LadLandingCoverCustom',
  components: {
    LadImage: () =>
      import(
        /* webpackChunkName: "lad-image" */ '@/components/frontends/Image/index'
      ),
    LadExperienceGallery: () =>
      import(
        /* webpackChunkName: "lad-experience-gallery" */ '@/components/experience-gallery'
      ),
    LadVideoDialog,
  },
  mixins: [CacheComponentMixin, GenericEventMixin],
  data() {
    return {
      base: 'landing.cover',
      dialogs: {
        pictures: false,
        video: false,
      },
      activeIndex: 0,
    }
  },
  computed: {
    ...mapState('experience', {
      name: (state) => state.experience.name,
      exExperience: (state) => state.experience,
      hasVideo: (state) => !!state.experience.videos.website,
      totalAmount: (state) => state.sales_plan.total_amount,
      exCover: 'cover',
      exHighlights: 'highlights',
      exItinerary: (state) => state.itinerary,
    }),
    ...mapState('settings', ['logo', 'currency']),
    getHighlights() {
      return this.exHighlights.join(' ')
    },
    getLocationDay() {
      if (this.exItinerary.length > 0) {
        return this.exItinerary[this.activeIndex].location?.name
      }
      return null
    },
    ...mapGetters('device', ['isMobile']),
  },
  methods: {
    async handleClickLogo() {
      if (this.$route.name.startsWith('landing-permalink')) {
        const data = {
          name: 'go_homepage',
          currency: this.setting.currency,
          experience: {
            ...this.exExperience,
            total_amount: this.totalAmount,
          },
        }

        await this.sendGenericEvent(data)
      }
    },
    handleChangeCarousel(index) {
      this.activeIndex = index // Actualizar el índice activo
    },
    handleShowPictures() {
      this.dialogs.pictures = true
    },
    handleClosePictures() {
      this.dialogs.pictures = false
    },
    handleShowVideo() {
      this.dialogs.video = true
    },
    handleCloseVideo() {
      this.dialogs.video = false
    },
    getImageDay(itinerary) {
      if (itinerary.images.length > 0) {
        return itinerary.images[0].url
      }
      return null
    },
  },
}
</script>

<style lang="scss">
.lad-landing-cover__custom {
  position: relative;

  .el-carousel {
    z-index: 0;

    &__container {
      min-height: 100vh;

      @media screen and (min-width: 1027px) {
        min-height: calc(100vh - 94px);
      }
    }
  }

  img {
    height: 100% !important;
    width: 100%;
    object-fit: cover;
    display: block;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),
      linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 80.82%);
  }

  &__content {
    position: absolute;
    padding: 24px 20px 32px;
    bottom: 0;
    color: #fff;
    display: grid;
    align-content: space-between;
    height: 100%;

    @media screen and (min-width: 768px) {
      max-width: 60%;
      padding: 32px 115px 50px;
    }

    .landing-url-home {
      display: inline-block;

      .landing-logo {
        max-height: 62px;
        max-width: 120px;
      }
    }

    h2 {
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      margin: 0 0 10px;
    }

    h1 {
      font-size: 30px;
      font-weight: 700;
      line-height: 36px;
      margin: 0 0 10px;
    }

    p {
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      margin: 0 0 20px;
    }

    .medias {
      display: grid;
      align-items: center;
      grid-template-columns: auto 90px;

      @media screen and (min-width: 768px) {
        grid-template-columns: none;
      }

      span {
        font-size: 14px;
        font-weight: 700;
        line-height: 16.8px;
      }

      .actions {
        display: grid;
        grid-template-columns: 36px 1fr 36px;

        @media screen and (min-width: 768px) {
          grid-template-columns: 120px 20px 120px;
          margin-top: 80px;
        }

        &.without-video {
          justify-content: right;
          grid-template-columns: 36px;

          @media screen and (min-width: 768px) {
            justify-content: left;
            grid-template-columns: none;
          }
        }

        .el-button {
          background-color: #0a960c80;
          border: unset;
          color: #fff;
        }
      }
    }
  }
}
</style>
